import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RecruiterLayout from '../components/RecruiterLayout';
import { 
  MapPin, Mail, Phone, ArrowLeft, Briefcase, DollarSign, 
  Globe, Calendar, Clock, User, Star, Download, Eye,
  Building, Languages, FileText, CheckCircle, Video, X, ChevronDown, ChevronUp,
  Play, FileAudio, FileCheck, MailX
} from 'lucide-react';
import MatchReport from '../components/MatchReport';
import AsyncInterviewCard from '../components/AsyncInterviewCard';
import EmailTemplateModal from '../components/EmailTemplateModal';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';

const CandidateDetail = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  
  const [prospect, setProspect] = useState(null);
  const [application, setApplication] = useState(null);
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  
  // Invite to Call Modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [meetingTool, setMeetingTool] = useState('google_meet'); // google_meet | zoom | teams
  const [manualMeetLink, setManualMeetLink] = useState('');
  const [inviteForm, setInviteForm] = useState({
    date: '',
    startTime: '10:00',
    duration: '30',
    title: '',
    description: ''
  });

  // Email template modal state
  const [emailModalType, setEmailModalType] = useState(null); // 'contract' | 'rejection' | null

  // Interview recording state
  const [interviewVideo, setInterviewVideo] = useState(null);
  const [interviewSummary, setInterviewSummary] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [processingVideo, setProcessingVideo] = useState(false);
  const [recordInterview, setRecordInterview] = useState(true);
  const [upcomingInterview, setUpcomingInterview] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Stages that allow inviting to call — includes Screening (reviewing)
  // so recruiters can schedule interview invites straight from screening.
  const interviewStages = ['reviewing', 'shortlisted', 'interviewed', 'onsite_interview'];

  // Pipeline stages mapping
  const pipelineStages = {
    'pending': { label: 'All Applications', color: 'bg-pink-400', borderColor: 'border-pink-500' },
    'reviewing': { label: 'Screening', color: 'bg-purple-400', borderColor: 'border-purple-500' },
    'shortlisted': { label: 'Interview Recruiter', color: 'bg-sky-400', borderColor: 'border-sky-500' },
    'interviewed': { label: 'Interview Hiring', color: 'bg-blue-400', borderColor: 'border-blue-500' },
    'onsite_interview': { label: 'Onsite Interview', color: 'bg-blue-600', borderColor: 'border-blue-700' },
    'decision': { label: 'Decision', color: 'bg-amber-400', borderColor: 'border-amber-500' },
    'offered': { label: 'Contract', color: 'bg-green-500', borderColor: 'border-green-600' },
    'accepted': { label: 'Contract', color: 'bg-green-500', borderColor: 'border-green-600' },
    'rejected': { label: 'Rejected', color: 'bg-orange-400', borderColor: 'border-orange-500' },
    'withdrawn': { label: 'Withdrawn', color: 'bg-red-500', borderColor: 'border-red-600' }
  };

  useEffect(() => {
    fetchApplicationDetails();
  }, [applicationId, userProfile]);

  useEffect(() => {
    if (applicationId) fetchInterviewData();
  }, [applicationId]);

  // Auto-poll while an interview is processing (live bot or async pipeline)
  useEffect(() => {
    if (!interviewVideo) return;
    const isProcessing =
      interviewVideo.status === 'processing' ||
      (interviewVideo.bot_id && !interviewVideo.recording_url) ||
      (interviewVideo.summary?.is_async && interviewVideo.summary?.processing && !interviewVideo.recording_url);
    if (!isProcessing) return;
    const iv = setInterval(() => { fetchInterviewData(); }, 8000);
    return () => clearInterval(iv);
  }, [interviewVideo?.id, interviewVideo?.status, interviewVideo?.recording_url]);

  const fetchInterviewData = async () => {
    try {
      // Fetch video recordings
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/video-calls/application/${applicationId}`
      );
      if (res.data.success && res.data.data?.length > 0) {
        const calls = res.data.data;
        
        // Check for unprocessed bots and trigger processing
        for (const call of calls) {
          if (call.bot_id && call.status === 'bot_joining') {
            try {
              await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/video-calls/process-bot/${call.bot_id}`);
            } catch (e) { /* bot may not be done yet */ }
          }
        }

        // Re-fetch after processing
        const refreshed = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/video-calls/application/${applicationId}`
        );
        const refreshedCalls = refreshed.data.data || [];
        
        // Find the latest completed recording (with playable URL)
        const completed = refreshedCalls.find(c => c.status === 'completed' && c.recording_url);
        if (completed) {
          setInterviewVideo(completed);
          if (completed.summary) {
            setInterviewSummary(typeof completed.summary === 'string' ? JSON.parse(completed.summary) : completed.summary);
          }
        } else {
          // Prefer an in-progress async stub (so the UI shows the processing spinner)
          const asyncProcessing = refreshedCalls.find(c => c.summary?.is_async && !c.recording_url);
          setInterviewVideo(asyncProcessing || refreshedCalls[0]);
        }
      }
    } catch (err) {
      // Table may not exist yet
    }

    try {
      // Fetch upcoming interview for this application
      const invRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/interviews/application/${applicationId}`
      );
      if (invRes.data.success && invRes.data.data) {
        setUpcomingInterview(invRes.data.data);
      }
    } catch (err) {
      // No interviews yet
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingVideo(true);
      // Upload the video file
      const formData = new FormData();
      formData.append('file', file);
      formData.append('applicationId', applicationId);

      const uploadRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/video-calls/upload-interview`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (uploadRes.data.success) {
        setInterviewVideo(uploadRes.data.data);
        setUploadingVideo(false);
        setProcessingVideo(true);

        // Trigger transcription + summary
        const summaryRes = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/video-calls/process-interview`,
          { videoCallId: uploadRes.data.data.id, applicationId }
        );

        if (summaryRes.data.success && summaryRes.data.data?.summary) {
          setInterviewSummary(summaryRes.data.data.summary);
          setInterviewVideo(prev => ({ ...prev, status: 'completed', summary: summaryRes.data.data.summary }));
        }
      }
    } catch (err) {
      console.error('Error uploading interview video:', err);
      alert('Failed to upload video. Please try again.');
    } finally {
      setUploadingVideo(false);
      setProcessingVideo(false);
    }
  };

  const fetchApplicationDetails = async () => {
    try {
      setLoading(true);
      setNotFound(false);
      
      // Fetch application details
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/${applicationId}`
      );
      
      if (response.data.success) {
        const appData = response.data.data;
        setApplication(appData);
        setPosition(appData.positions);

        // Agency / ATS submissions store the candidate on the application itself
        // (no linked prospect). Build a prospect-shaped object from the application.
        const fromApp = () => ({
          firstName: appData.first_name,
          lastName: appData.last_name,
          email: appData.email,
          phone: appData.phone,
          location: appData.location,
          currentJobTitle: appData.current_job_title,
          currentCompany: appData.current_company,
          resumeUrl: appData.resume_url,
          linkedinUrl: appData.linkedin_profile,
        });

        if (appData.prospect_id) {
          try {
            const prospectResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/prospects/user/${appData.prospect_id}`
            );
            setProspect(prospectResponse.data?.success && prospectResponse.data.data
              ? prospectResponse.data.data
              : fromApp());
          } catch (e) {
            setProspect(fromApp());
          }
        } else {
          setProspect(fromApp());
        }
      }
    } catch (error) {
      console.error('Error fetching application details:', error);
      // 404 → the application was deleted/withdrawn from the platform
      if (error?.response?.status === 404) {
        setNotFound(true);
        setProspect(null);
        setApplication(null);
        setPosition(null);
        return;
      }
      // Fallback: try to use data passed from navigation state (e.g., MyCandidates)
      if (location.state?.candidate) {
        const candidate = location.state.candidate;
        setProspect({
          firstName: candidate.firstName,
          lastName: candidate.lastName,
          email: candidate.email,
          phone: candidate.phone,
          location: candidate.location,
          currentJobTitle: candidate.currentJobTitle,
          currentCompany: candidate.currentCompany,
          skills: candidate.skills || [],
          languages: candidate.languages || candidate.spokenLanguages || [],
          expectedSalaryMin: candidate.expectedSalary,
        });
        setApplication({
          id: candidate.applicationId,
          status: candidate.applicationStatus || candidate.status,
          applied_at: candidate.appliedAt,
          cover_letter: candidate.coverLetter,
        });
        setPosition({
          title: candidate.appliedPosition || candidate.positionTitle,
          company_name: candidate.companyName,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      setStatusUpdating(true);
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/${applicationId}/status`,
        { status: newStatus }
      );
      
      setApplication(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleNotesUpdate = async (notes) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/${applicationId}/notes`,
        { notes }
      );
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  const handleOpenInviteModal = () => {
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toISOString().split('T')[0];
    
    // Set default title based on stage
    const stageLabel = pipelineStages[application?.status]?.label || 'Interview';
    const defaultTitle = `${stageLabel} - ${prospect?.firstName} ${prospect?.lastName} for ${position?.title}`;
    
    setInviteForm({
      date: dateStr,
      startTime: '10:00',
      duration: '30',
      title: defaultTitle,
      description: `Interview for ${position?.title} position at ${position?.company_name || 'our company'}.`
    });
    setShowInviteModal(true);
  };

  const handleSendInvite = async () => {
    try {
      setInviteLoading(true);
      
      const startDateTime = new Date(`${inviteForm.date}T${inviteForm.startTime}:00`);
      const endDateTime = new Date(startDateTime.getTime() + parseInt(inviteForm.duration) * 60000);
      
      let meetLink = null;
      let calendarEventId = null;

      if (meetingTool === 'google_meet') {
        // Create calendar event with Google Meet link auto-generated
        const calendarResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/calendar/events`,
          {
            userId: userProfile.id,
            title: inviteForm.title,
            start: startDateTime.toISOString(),
            end: endDateTime.toISOString(),
            description: inviteForm.description,
            attendees: [prospect?.email].filter(Boolean),
            conferenceData: true
          }
        );

        if (!calendarResponse.data?.success) {
          throw new Error(calendarResponse.data?.message || 'Failed to create calendar event');
        }

        meetLink = calendarResponse.data?.event?.meetLink;
        calendarEventId = calendarResponse.data?.event?.id;
      } else {
        // Zoom or Teams — use the manually provided link
        meetLink = manualMeetLink.trim() || null;
      }

      // Save interview invitation
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/interviews/create`,
        {
          prospectId: application?.prospect_id,
          positionId: application?.position_id,
          applicationId: applicationId,
          recruiterId: userProfile.id,
          interviewDate: startDateTime.toISOString(),
          durationMinutes: parseInt(inviteForm.duration),
          interviewType: meetingTool,
          title: inviteForm.title,
          description: inviteForm.description,
          meetLink: meetLink || null,
          calendarEventId: calendarEventId
        }
      );

      // Send Recall.ai recording bot if enabled and we have a meeting link
      if (meetLink && recordInterview) {
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/video-calls/send-bot`,
            {
              meetingUrl: meetLink,
              applicationId: applicationId,
              title: inviteForm.title
            }
          );
        } catch (botErr) {
          console.warn('Recording bot could not be sent:', botErr);
        }
      }

      const toolName = meetingTool === 'google_meet' ? 'Google Meet' : meetingTool === 'zoom' ? 'Zoom' : 'Teams';
      alert(`Interview invitation sent via ${toolName}!` + (meetLink && recordInterview ? ' Recording bot will join automatically.' : ''));
      setShowInviteModal(false);
      fetchInterviewData();
    } catch (error) {
      console.error('Error sending invite:', error);
      if (error.response?.status === 401) {
        alert('Please connect your Google Calendar in Settings first.');
      } else {
        alert('Failed to send invitation. Please try again.');
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const getCurrentStage = () => {
    const status = application?.status || 'pending';
    return pipelineStages[status] || pipelineStages['pending'];
  };

  if (loading) {
    return (
      <RecruiterLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">Loading candidate details...</p>
        </div>
      </RecruiterLayout>
    );
  }

  if (notFound) {
    return (
      <RecruiterLayout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div
            className="bg-white border border-gray-200 rounded-2xl p-10 text-center shadow-sm"
            data-testid="application-not-found"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-5">
              <MailX className="w-7 h-7 text-amber-500" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              Application no longer exists
            </h1>
            <p className="text-sm text-gray-500 leading-relaxed max-w-md mx-auto mb-6">
              This application is no longer on the platform. The candidate may have
              withdrawn their application, or it was removed by an administrator.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => navigate('/candidates')}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                data-testid="application-not-found-candidates-btn"
              >
                Back to Candidates
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 rounded-lg border border-gray-200 hover:border-gray-300"
                data-testid="application-not-found-dashboard-btn"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </RecruiterLayout>
    );
  }

  if (!prospect && !application) {
    return (
      <RecruiterLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">Candidate not found</p>
          <button 
            onClick={() => navigate('/candidates')}
            className="mt-4 text-blue-600 hover:underline block mx-auto"
          >
            Back to Candidates
          </button>
        </div>
      </RecruiterLayout>
    );
  }

  const currentStage = getCurrentStage();

  return (
    <RecruiterLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/candidates')}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-medium text-sm"
          data-testid="back-to-candidates"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Candidates
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start gap-4 mb-6">
                {/* Profile Picture */}
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-xl">
                    {prospect?.firstName?.[0]}{prospect?.lastName?.[0]}
                  </span>
                </div>

                {/* Basic Info */}
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="candidate-name">
                    {prospect?.lastName}, {prospect?.firstName}
                  </h1>
                  <p className="text-gray-600 mb-3">
                    {prospect?.currentJobTitle || prospect?.jobTitle || 'Job Title Not Specified'}
                  </p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span>
                    € {prospect?.expectedSalaryMin ? prospect.expectedSalaryMin.toLocaleString() : '0'}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="capitalize">{prospect?.workType || 'Full-time'}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Languages className="w-4 h-4 text-gray-400" />
                  <span>{prospect?.languages?.join(', ') || 'Not specified'}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{prospect?.location || 'Location not specified'}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Building className="w-4 h-4 text-gray-400" />
                  <span className="capitalize">{prospect?.workArrangement || 'Hybrid'}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="capitalize">{prospect?.experienceLevel || 'Middle'}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Briefcase className="w-4 h-4 text-gray-400" />
                  <span>{prospect?.currentCompany || 'Not specified'}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>{prospect?.availability || '1 Month'}</span>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Documents</h2>
              <div className="space-y-3">
                {prospect?.resumeUrl ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-blue-500" />
                      <span className="text-gray-700 font-medium">CV</span>
                      <span className="text-gray-500 text-sm">CV_{prospect.lastName}_{prospect.firstName}.pdf</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={prospect.resumeUrl}
                        download
                        className="text-blue-600 hover:text-blue-700 text-sm underline"
                      >
                        download
                      </a>
                      <a
                        href={prospect.resumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-sm underline"
                      >
                        preview
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">CV - Not uploaded</div>
                )}

                {prospect?.diplomaUrl ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700 font-medium">Diplom</span>
                      <span className="text-gray-500 text-sm">Diploma_{prospect.lastName}_{prospect.firstName}.pdf</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={prospect.diplomaUrl}
                        download
                        className="text-blue-600 hover:text-blue-700 text-sm underline"
                      >
                        download
                      </a>
                      <a
                        href={prospect.diplomaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-sm underline"
                      >
                        preview
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">Diplom - Not uploaded</div>
                )}

                {/* Additional Documents from application */}
                {(() => {
                  let docs = [];
                  try {
                    const report = typeof application?.match_report === 'string'
                      ? JSON.parse(application.match_report)
                      : application?.match_report;
                    if (report?.additional_documents) docs = report.additional_documents;
                  } catch {}
                  // Also check notes as fallback
                  if (docs.length === 0) {
                    try {
                      const notes = typeof application?.notes === 'string'
                        ? JSON.parse(application.notes)
                        : null;
                      if (notes?.additional_documents) docs = notes.additional_documents;
                    } catch {}
                  }
                  return docs.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between" data-testid={`additional-doc-${idx}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-purple-500" />
                        <span className="text-gray-700 font-medium">Document</span>
                        <span className="text-gray-500 text-sm truncate max-w-[200px]">{doc.name || `Document_${idx + 1}`}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={doc.url}
                          download
                          className="text-blue-600 hover:text-blue-700 text-sm underline"
                        >
                          download
                        </a>
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-sm underline"
                        >
                          preview
                        </a>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Cover Letter */}
            {application?.cover_letter && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Cover Letter</h2>
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                  {application.cover_letter}
                </p>
              </div>
            )}

            {/* AI Match Report */}
            {application?.match_report && (() => {
              const report = typeof application.match_report === 'string'
                ? (() => { try { return JSON.parse(application.match_report); } catch { return null; } })()
                : application.match_report;
              if (!report) return null;

              return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-testid="match-report-section">
                  <div className="flex items-center justify-between px-6 pt-6 pb-2">
                    <h2 className="text-lg font-semibold text-gray-900">Match Report</h2>
                    <button
                      onClick={() => setReportOpen(o => !o)}
                      className="w-8 h-8 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center hover:bg-blue-100 transition-colors"
                      data-testid="match-report-toggle"
                    >
                      {reportOpen
                        ? <ChevronUp className="w-4 h-4 text-blue-600" />
                        : <ChevronDown className="w-4 h-4 text-blue-600" />
                      }
                    </button>
                  </div>
                  <MatchReport 
                    data={report} 
                    candidateName={prospect ? `${prospect.firstName || ''} ${prospect.lastName || ''}`.trim() : undefined}
                    candidateInfo={{
                      noticePeriod: prospect?.availability || application?.notice_period,
                      salaryExpectation: prospect?.expectedSalaryMin || application?.salary_expectation,
                      locationCity: prospect?.location || application?.location,
                    }}
                    collapsed={!reportOpen}
                  />
                </div>
              );
            })()}

            {/* Interview Summary Card */}
            {interviewSummary && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="interview-summary-card">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Interview Summary</h2>
                    <p className="text-xs text-gray-500 mt-0.5">Generated from the recording</p>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Speaker time doughnut — clearly shows who spoke more */}
                    {interviewSummary.speakers && interviewSummary.speakers.total_seconds > 0 && (() => {
                      const sp = interviewSummary.speakers;
                      const total = sp.total_seconds || 1;
                      const recPct = (sp.recruiter_seconds / total) * 100;
                      const candPct = 100 - recPct;
                      const r = 32, c = 36, circ = 2 * Math.PI * r;
                      const recLen = (recPct / 100) * circ;
                      const candLen = (candPct / 100) * circ;
                      const dominant = recPct >= candPct ? 'Recruiter' : 'Candidate';
                      const domPct = Math.max(recPct, candPct);
                      const fmt = (s) => {
                        const m = Math.floor(s / 60); const ss = Math.round(s % 60);
                        return m > 0 ? `${m}m ${ss}s` : `${ss}s`;
                      };
                      return (
                        <div className="flex items-center gap-3" data-testid="speaker-chart" title={`Recruiter ${fmt(sp.recruiter_seconds)} (${recPct.toFixed(0)}%) · Candidate ${fmt(sp.candidate_seconds)} (${candPct.toFixed(0)}%)`}>
                          <svg width={c * 2} height={c * 2}>
                            {/* Recruiter slice (blue) */}
                            <circle
                              cx={c} cy={c} r={r}
                              fill="none"
                              stroke="#2563eb"
                              strokeWidth="10"
                              strokeDasharray={`${recLen} ${circ}`}
                              transform={`rotate(-90 ${c} ${c})`}
                            />
                            {/* Candidate slice (amber) — starts where recruiter ends */}
                            <circle
                              cx={c} cy={c} r={r}
                              fill="none"
                              stroke="#f59e0b"
                              strokeWidth="10"
                              strokeDasharray={`${candLen} ${circ}`}
                              strokeDashoffset={-recLen}
                              transform={`rotate(-90 ${c} ${c})`}
                            />
                            <text x={c} y={c - 1} textAnchor="middle" className="fill-gray-900" style={{ fontSize: 14, fontWeight: 700 }}>
                              {domPct.toFixed(0)}%
                            </text>
                            <text x={c} y={c + 12} textAnchor="middle" className="fill-gray-500" style={{ fontSize: 8, letterSpacing: 0.4 }}>
                              {dominant.toUpperCase()}
                            </text>
                          </svg>
                          <div className="text-[11px] leading-relaxed">
                            <div className="flex items-center gap-1.5 text-gray-700">
                              <span className="w-2.5 h-2.5 rounded-sm bg-blue-600 inline-block" />
                              <span className="font-medium">Recruiter</span>
                              <span className="text-gray-500 tabular-nums">{recPct.toFixed(0)}% · {fmt(sp.recruiter_seconds)}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-gray-700">
                              <span className="w-2.5 h-2.5 rounded-sm bg-amber-500 inline-block" />
                              <span className="font-medium">Candidate</span>
                              <span className="text-gray-500 tabular-nums">{candPct.toFixed(0)}% · {fmt(sp.candidate_seconds)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    {interviewSummary.score != null && (
                      <span className={`text-sm font-semibold px-2.5 py-1 rounded-md ${interviewSummary.score >= 70 ? 'bg-green-50 text-green-700' : interviewSummary.score >= 50 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                        {interviewSummary.score}/100
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  {interviewSummary.overall_impression && (
                    <p className="text-sm text-gray-700 leading-relaxed">{interviewSummary.overall_impression}</p>
                  )}
                  {interviewSummary.role_fit_assessment && (
                    <div className="border-l-2 border-blue-200 pl-3 py-1">
                      <p className="text-xs font-medium text-gray-500 mb-0.5">Fit for the role</p>
                      <p className="text-sm text-gray-700 leading-relaxed">{interviewSummary.role_fit_assessment}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {interviewSummary.strengths?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-1.5">Strengths</h4>
                        <ul className="space-y-1">
                          {interviewSummary.strengths.map((s, i) => (
                            <li key={i} className="text-sm text-gray-600 flex items-start gap-1.5">
                              <CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" /> {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {interviewSummary.concerns?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-1.5">Concerns</h4>
                        <ul className="space-y-1">
                          {interviewSummary.concerns.map((c, i) => (
                            <li key={i} className="text-sm text-gray-600 flex items-start gap-1.5">
                              <span className="text-amber-500 mt-0 leading-none text-base flex-shrink-0">·</span> {c}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  {(interviewSummary.recommended_next_step || interviewSummary.recommended_next_steps) && (
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-sm text-gray-700"><span className="font-medium">Next step: </span>{interviewSummary.recommended_next_step || interviewSummary.recommended_next_steps}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Skills */}
            {prospect?.skills && prospect.skills.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Skills</h2>
                <div className="flex flex-wrap gap-2">
                  {prospect.skills.map((skill, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-blue-500 text-white rounded text-sm font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* About */}
            {prospect?.bio && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">About</h2>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {prospect.bio}
                </p>
              </div>
            )}

            {/* Internal Notes */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Internal Notes (Private to Your Company)</h2>
              <p className="text-sm text-gray-500 mb-3">
                These notes are only visible to your organization and will not be shared with the candidate.
              </p>
              <textarea
                defaultValue={application?.notes || ''}
                onBlur={(e) => handleNotesUpdate(e.target.value)}
                placeholder="Add notes about this candidate, interview feedback, recommendations, etc."
                className="w-full h-32 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                data-testid="internal-notes"
              />
              <p className="text-xs text-gray-400 mt-2">
                Notes are auto-saved when you click outside the text area
              </p>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Status Card */}
            <Card data-testid="status-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`p-3 rounded-lg ${currentStage.color} text-white mb-4`}>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">{currentStage.label}</span>
                  </div>
                </div>
                <Select 
                  value={application?.status || 'pending'} 
                  onValueChange={handleStatusUpdate}
                  disabled={statusUpdating}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder="Change status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">All Applications</SelectItem>
                    <SelectItem value="reviewing">Screening</SelectItem>
                    <SelectItem value="shortlisted">Interview Recruiter</SelectItem>
                    <SelectItem value="interviewed">Interview Hiring</SelectItem>
                    <SelectItem value="onsite_interview">Onsite Interview</SelectItem>
                    <SelectItem value="decision">Decision</SelectItem>
                    <SelectItem value="offered">Contract</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="withdrawn">Withdrawn</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Invite to Call button - only show for interview stages */}
                {interviewStages.includes(application?.status) && (
                  <Button
                    onClick={handleOpenInviteModal}
                    className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white"
                    data-testid="invite-to-call-btn"
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Invite to Call
                  </Button>
                )}

                {/* Contract email — when stage is offered/accepted */}
                {(application?.status === 'offered' || application?.status === 'accepted') && (
                  <Button
                    onClick={() => setEmailModalType('contract')}
                    className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white"
                    data-testid="send-contract-email-btn"
                  >
                    <FileCheck className="w-4 h-4 mr-2" />
                    Send contract email
                  </Button>
                )}

                {/* Rejection email — when stage is rejected */}
                {application?.status === 'rejected' && (
                  <Button
                    onClick={() => setEmailModalType('rejection')}
                    variant="outline"
                    className="w-full mt-3 border-gray-300 text-gray-700 hover:bg-gray-50"
                    data-testid="send-rejection-email-btn"
                  >
                    <MailX className="w-4 h-4 mr-2" />
                    Send rejection email
                  </Button>
                )}

                {/* Last sent indicator */}
                {(() => {
                  let notes = {};
                  try { notes = typeof application?.notes === 'string' ? JSON.parse(application.notes || '{}') : (application?.notes || {}); } catch {}
                  const sent = Array.isArray(notes.emails_sent) ? notes.emails_sent : [];
                  const last = sent.length ? sent[sent.length - 1] : null;
                  if (!last) return null;
                  const ago = Math.max(1, Math.round((Date.now() - new Date(last.sent_at).getTime()) / 60000));
                  const agoText = ago < 60 ? `${ago}m ago` : ago < 1440 ? `${Math.round(ago / 60)}h ago` : `${Math.round(ago / 1440)}d ago`;
                  return (
                    <p className="text-[11px] text-gray-500 mt-3 leading-tight" data-testid="last-email-sent">
                      Last {last.type} email sent {agoText} {last.ok === false ? '· delivery pending' : ''}
                    </p>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Application Card */}
            <Card data-testid="application-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">Application</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900" data-testid="applied-position">
                        {position?.title || 'Position'}
                      </p>
                      <p className="text-xs text-gray-600" data-testid="applied-company">
                        {position?.company_name || 'Company'}
                      </p>
                      {position?.location && (
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {position.location}
                        </p>
                      )}
                      {(position?.salary_min || position?.salary_max) && (
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <DollarSign className="w-3 h-3" />
                          €{position.salary_min?.toLocaleString()}
                          {position.salary_max && ` - €${position.salary_max.toLocaleString()}`}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        Applied: {application?.applied_at 
                          ? new Date(application.applied_at).toLocaleDateString('de-DE')
                          : 'N/A'}
                      </p>
                      {(application?.match_score !== null && application?.match_score !== undefined) && (
                        <div className="mt-2" data-testid="sidebar-match-score">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                            application.match_score >= 75 ? 'text-green-700 bg-green-100' :
                            application.match_score >= 50 ? 'text-yellow-700 bg-yellow-100' :
                            'text-red-700 bg-red-100'
                          }`}>
                            {application.match_score}% AI Match
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Interview Video Card */}
            <Card data-testid="interview-video-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  Interview Video
                  {interviewVideo?.summary?.is_async && (
                    <span className="text-[10px] font-medium uppercase tracking-wide bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded" data-testid="async-badge">Video</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {interviewVideo && interviewVideo.status === 'completed' && interviewVideo.recording_url ? (
                  <div className="space-y-2">
                    <div className="relative rounded-lg overflow-hidden bg-black">
                      <video
                        src={interviewVideo.recording_url}
                        controls
                        className="w-full h-auto max-h-[180px] object-contain"
                        data-testid="interview-video-player"
                      />
                      <button
                        onClick={() => setShowVideoModal(true)}
                        className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded transition-colors"
                        title="Expand"
                        data-testid="expand-video-btn"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500 truncate">{interviewVideo.title || 'Recording'}</p>
                      <label className="text-xs text-blue-600 cursor-pointer hover:underline flex-shrink-0">
                        Replace
                        <input type="file" accept="video/*,audio/*" onChange={handleVideoUpload} className="hidden" />
                      </label>
                    </div>
                  </div>
                ) : uploadingVideo || processingVideo ? (
                  <div className="text-center py-3">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-xs text-gray-500">{uploadingVideo ? 'Uploading...' : 'Transcribing & summarizing...'}</p>
                  </div>
                ) : interviewVideo && interviewVideo.summary?.is_async && !interviewVideo.recording_url ? (
                  <div className="text-center py-3" data-testid="async-processing">
                    <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-xs text-gray-700 font-medium">
                      {interviewVideo.status === 'failed' || interviewVideo.summary?.stitch_failed
                        ? 'Video processing did not finish'
                        : 'Processing async recording…'}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {interviewVideo.status === 'failed' || interviewVideo.summary?.stitch_failed
                        ? 'Click below to retry the stitch + summary'
                        : 'Stitching answers and generating summary'}
                    </p>
                    {interviewVideo.summary?.async_invite_id && (
                      <button
                        onClick={async () => {
                          try {
                            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/async-interviews/${interviewVideo.summary.async_invite_id}/reprocess`);
                            toast.success('Reprocess queued — refresh in ~30 seconds');
                          } catch {
                            toast.error('Reprocess failed to queue');
                          }
                        }}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-purple-700 hover:text-purple-900 hover:underline"
                        data-testid="async-reprocess-btn"
                      >
                        Reprocess now
                      </button>
                    )}
                  </div>
                ) : interviewVideo && interviewVideo.bot_id && !interviewVideo.recording_url ? (
                  <div className="text-center py-3" data-testid="recording-processing">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-xs text-gray-500">Processing recording…</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Refresh in ~1 minute</p>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-sm text-gray-400 mb-2">No interview video</p>
                    <label className="inline-flex items-center gap-1.5 text-xs text-blue-600 cursor-pointer hover:text-blue-700 font-medium" data-testid="upload-video-btn">
                      <Video className="w-3.5 h-3.5" />
                      Upload recording
                      <input type="file" accept="video/*,audio/*" onChange={handleVideoUpload} className="hidden" />
                    </label>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Event Card */}
            <Card data-testid="event-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">Upcoming Interview</CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingInterview ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-800">{upcomingInterview.title}</p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" />
                        <span>{new Date(upcomingInterview.interview_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(upcomingInterview.interview_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} ({upcomingInterview.duration_minutes} min)</span>
                      </div>
                    </div>
                    {upcomingInterview.meet_link && (
                      <a
                        href={upcomingInterview.meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-blue-600 font-medium hover:underline mt-2"
                        data-testid="join-meeting-link"
                      >
                        <Video className="w-3.5 h-3.5" />
                        Join Meeting
                      </a>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No upcoming interview</p>
                )}
              </CardContent>
            </Card>

            {/* Async Interviews Card — under Upcoming Interview */}
            <AsyncInterviewCard
              applicationId={application?.id || applicationId}
              candidate={{
                applicationId: application?.id || applicationId,
                prospectId: application?.prospect_id || prospect?.id,
                appliedPositionId: application?.position_id,
                appliedPosition: application?.positions?.title,
                firstName: prospect?.first_name || prospect?.firstName,
                lastName: prospect?.last_name || prospect?.lastName,
              }}
              recruiterId={userProfile?.id}
            />

            {/* Also Suitable */}
            {prospect?.preferredJobTitles && prospect.preferredJobTitles.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold">Also suitable</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {prospect.preferredJobTitles.map((title, idx) => (
                      <p key={idx} className="text-sm text-gray-700">{title}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons intentionally removed — Present Position + Contact Prospect
                (see git history if we ever need to restore) */}
          </div>
        </div>
      </div>

      {/* Email Template Modal (Contract / Rejection) */}
      <EmailTemplateModal
        open={!!emailModalType}
        onOpenChange={(o) => { if (!o) setEmailModalType(null); }}
        type={emailModalType || 'contract'}
        application={{ id: application?.id, users: { first_name: prospect?.firstName, last_name: prospect?.lastName, email: prospect?.email }, notes: application?.notes }}
        recruiterId={userProfile?.id}
        organizationId={position?.organization_id || userProfile?.organizationId || userProfile?.organization_id}
        onSent={({ type }) => {
          // Refresh application so we get the latest emails_sent log
          fetchInterviewData?.();
          // Show lightweight feedback
          try { alert(`${type === 'contract' ? 'Contract' : 'Rejection'} email sent.`); } catch {}
        }}
      />

      {/* Invite to Call Modal - Outlook Style */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-[480px] shadow-2xl" style={{ borderRadius: '2px' }}>
            {/* Header - Blue bar like Outlook */}
            <div className="bg-blue-600 px-4 py-3 flex items-center justify-between">
              <h3 className="text-white font-medium">New Meeting</h3>
              <button 
                onClick={() => setShowInviteModal(false)}
                className="text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-5">
              {/* Title Field */}
              <div className="mb-4">
                <input
                  type="text"
                  value={inviteForm.title}
                  onChange={(e) => setInviteForm({ ...inviteForm, title: e.target.value })}
                  placeholder="Add a title"
                  className="w-full px-0 py-2 border-0 border-b-2 border-gray-200 focus:border-blue-600 focus:outline-none text-lg font-medium placeholder-gray-400"
                  data-testid="invite-title"
                />
              </div>

              {/* Attendee */}
              <div className="flex items-center gap-3 py-3 border-b border-gray-100">
                <User className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <span className="text-sm text-gray-900">{prospect?.firstName} {prospect?.lastName}</span>
                  <span className="text-sm text-gray-400 ml-2">{prospect?.email}</span>
                </div>
              </div>

              {/* Date & Time Row */}
              <div className="flex items-center gap-3 py-3 border-b border-gray-100">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div className="flex items-center gap-4 flex-1">
                  <input
                    type="date"
                    value={inviteForm.date}
                    onChange={(e) => setInviteForm({ ...inviteForm, date: e.target.value })}
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-blue-500"
                    data-testid="invite-date"
                  />
                  <input
                    type="time"
                    value={inviteForm.startTime}
                    onChange={(e) => setInviteForm({ ...inviteForm, startTime: e.target.value })}
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-blue-500"
                    data-testid="invite-time"
                  />
                  <select
                    value={inviteForm.duration}
                    onChange={(e) => setInviteForm({ ...inviteForm, duration: e.target.value })}
                    className="px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-blue-500"
                    data-testid="invite-duration"
                  >
                    <option value="15">15 min</option>
                    <option value="30">30 min</option>
                    <option value="45">45 min</option>
                    <option value="60">1 hr</option>
                    <option value="90">1.5 hr</option>
                  </select>
                </div>
              </div>

              {/* Meeting Tool Selection */}
              <div className="py-3 border-b border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <Video className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-gray-700 font-medium">Meeting Tool</span>
                </div>
                <div className="flex items-center gap-2 ml-7">
                  <button
                    onClick={() => { setMeetingTool('google_meet'); setManualMeetLink(''); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border transition-colors ${
                      meetingTool === 'google_meet' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="tool-google-meet"
                  >
                    <img src="https://fonts.gstatic.com/s/i/productlogos/meet_2020q4/v1/web-96dp/logo_meet_2020q4_color_2x_web_96dp.png" alt="Meet" className="w-4 h-4" />
                    Google Meet
                  </button>
                  <button
                    onClick={() => { setMeetingTool('zoom'); setManualMeetLink(''); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border transition-colors ${
                      meetingTool === 'zoom' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="tool-zoom"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#2D8CFF"><path d="M4.585 6.836A2.25 2.25 0 0 0 2.25 9.072v5.856a2.25 2.25 0 0 0 2.335 2.236h9.58a2.25 2.25 0 0 0 2.335-2.236V9.072a2.25 2.25 0 0 0-2.335-2.236h-9.58Zm13.83 2.166 3.024-2.1a.75.75 0 0 1 1.186.612v9.072a.75.75 0 0 1-1.186.612l-3.024-2.1V9.002Z"/></svg>
                    Zoom
                  </button>
                  <button
                    onClick={() => { setMeetingTool('teams'); setManualMeetLink(''); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border transition-colors ${
                      meetingTool === 'teams' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="tool-teams"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#5B5FC7"><path d="M20.625 6.547h-5.344v3.89h5.344a.94.94 0 0 0 .938-.937V7.484a.94.94 0 0 0-.938-.937Zm-2.672 5.015h-2.672v4.922a2.11 2.11 0 0 0 2.11 2.11h.562a2.11 2.11 0 0 0 2.11-2.11V13.67a2.11 2.11 0 0 0-2.11-2.11ZM17.39 5.86a1.688 1.688 0 1 0 0-3.374 1.688 1.688 0 0 0 0 3.375ZM12.61 3.61a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm1.827 3.5H6.282A1.97 1.97 0 0 0 4.31 9.078v6.047a3.28 3.28 0 0 0 3.281 3.281h5.532a3.28 3.28 0 0 0 3.281-3.281V9.078a1.97 1.97 0 0 0-1.968-1.968Z"/></svg>
                    Teams
                  </button>
                </div>
                {(meetingTool === 'zoom' || meetingTool === 'teams') && (
                  <div className="mt-2 ml-7">
                    <input
                      type="text"
                      value={manualMeetLink}
                      onChange={(e) => setManualMeetLink(e.target.value)}
                      placeholder={`Paste your ${meetingTool === 'zoom' ? 'Zoom' : 'Teams'} meeting link`}
                      className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-blue-500"
                      data-testid="manual-meet-link"
                    />
                  </div>
                )}
              </div>

              {/* Record & Transcribe Toggle */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <FileAudio className="w-4 h-4 text-purple-500" />
                  <span className="text-sm text-gray-700">Auto-record & transcribe</span>
                </div>
                <button
                  onClick={() => setRecordInterview(!recordInterview)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${recordInterview ? 'bg-blue-600' : 'bg-gray-300'}`}
                  data-testid="record-toggle"
                >
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${recordInterview ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                </button>
              </div>
              {recordInterview && (
                <p className="text-xs text-green-600 flex items-center gap-1 py-1 px-1">
                  <CheckCircle className="w-3 h-3" />
                  A recording bot will join and capture the interview
                </p>
              )}

              {/* Description */}
              <div className="pt-3">
                <textarea
                  value={inviteForm.description}
                  onChange={(e) => setInviteForm({ ...inviteForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-blue-500 resize-none"
                  placeholder="Add a description or agenda..."
                  data-testid="invite-description"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 bg-gray-50 flex justify-end gap-2" style={{ borderRadius: '0 0 2px 2px' }}>
              <button
                onClick={() => setShowInviteModal(false)}
                disabled={inviteLoading}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded"
              >
                Discard
              </button>
              <button
                onClick={handleSendInvite}
                disabled={inviteLoading || !inviteForm.date || !inviteForm.startTime}
                className="px-5 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                data-testid="send-invite-btn"
              >
                {inviteLoading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Expand Modal */}
      {showVideoModal && interviewVideo?.recording_url && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setShowVideoModal(false)} data-testid="video-modal">
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-10 right-0 text-white/80 hover:text-white transition-colors"
              data-testid="close-video-modal"
            >
              <X className="w-6 h-6" />
            </button>
            <video
              src={interviewVideo.recording_url}
              controls
              autoPlay
              className="w-full rounded-lg shadow-2xl"
              data-testid="video-modal-player"
            />
            <p className="text-white/60 text-sm text-center mt-3">{interviewVideo.title || 'Interview Recording'}</p>
          </div>
        </div>
      )}
    </RecruiterLayout>
  );
};

export default CandidateDetail;
